import React, { useState } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { ChevronRightIcon, ArrowDownTrayIcon, EyeIcon, EyeSlashIcon } from '@heroicons/react/24/outline';
import { 
  diseaseHubApi, 
  type DiseasePriorityRequest, 
  type DiseaseAnalysisResult,
  type BiomarkerAnalysisResult 
} from '../../services/diseaseHubClient';
import { TargetXHeader } from '../../components/TargetXHeader';

export default function DiseaseHubFixed() {
  // State management
  const [selectedCategory, setSelectedCategory] = useState<string>('');
  const [priorityRequest, setPriorityRequest] = useState<DiseasePriorityRequest>({});
  const [selectedDisease, setSelectedDisease] = useState<string>('');
  const [analysisResults, setAnalysisResults] = useState<DiseaseAnalysisResult[]>([]);
  const [biomarkerResults, setBiomarkerResults] = useState<BiomarkerAnalysisResult[]>([]);
  const [, setShowQuickAnalysisPanel] = useState(false);
  const [, setShowBiomarkerPanel] = useState(false);
  const [resultsVisible, setResultsVisible] = useState(true);
  const [sidebarVisible, setSidebarVisible] = useState(true);

  // API queries
  const { data: categories = [], isLoading: categoriesLoading } = useQuery({
    queryKey: ['disease-categories'],
    queryFn: diseaseHubApi.getCategories
  });

  const { data: stats } = useQuery({
    queryKey: ['disease-stats'],
    queryFn: diseaseHubApi.getStats
  });

  // API mutations
  const prioritizeMutation = useMutation({
    mutationFn: diseaseHubApi.prioritizeDiseases,
    onSuccess: (data) => {
      setAnalysisResults(data);
      setShowQuickAnalysisPanel(true);
    }
  });

  const biomarkerMutation = useMutation({
    mutationFn: diseaseHubApi.analyzeBiomarkers,
    onSuccess: (data) => {
      setBiomarkerResults(data);
      setShowBiomarkerPanel(true);
    }
  });

  const handlePrioritizeClick = () => {
    if (selectedCategory) {
      prioritizeMutation.mutate({ 
        category: selectedCategory,
        ...priorityRequest
      });
    }
  };

  const handleBiomarkerAnalysis = () => {
    if (selectedDisease) {
      biomarkerMutation.mutate({ disease_id: selectedDisease });
    }
  };

  const [searchResults, setSearchResults] = React.useState<any[]>([]);
  const [isSearching, setIsSearching] = React.useState(false);
  const [searchError, setSearchError] = React.useState<string>('');

  const handleSearch = async (query: string) => {
    if (!query.trim()) {
      setSearchResults([]);
      return;
    }

    try {
      setIsSearching(true);
      setSearchError('');
      const results = await diseaseHubApi.getAllDiseases({ search: query });
      setSearchResults(results.slice(0, 10)); // Show top 10 results
    } catch (error) {
      console.error('Search error:', error);
      setSearchError('Failed to search diseases. Please try again.');
    } finally {
      setIsSearching(false);
    }
  };

  return (
    <div className="h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-purple-100 flex flex-col">
      {/* TargetX Header */}
      <TargetXHeader 
        title="Disease Hub" 
        subtitle="Advanced disease prioritization and biomarker analysis for precision targeting"
        searchPlaceholder="Search diseases, categories, biomarkers..."
        onSearch={handleSearch}
      />

      {/* Search Results Section */}
      {searchResults.length > 0 && (
        <div className="px-8 py-4 bg-white shadow-sm border-b border-gray-200">
          <div className="max-w-6xl mx-auto">
            <h3 className="text-sm font-semibold text-gray-700 mb-3">Search Results</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {searchResults.map((disease: any) => (
                <div 
                  key={disease.id} 
                  className="p-4 bg-purple-50 rounded-lg border border-purple-200 cursor-pointer hover:bg-purple-100 transition-colors"
                  onClick={() => {
                    // You could add logic to select/filter by this disease
                    setSearchResults([]);
                  }}
                >
                  <div className="font-semibold text-purple-900">{disease.name}</div>
                  <div className="text-sm text-gray-600">{disease.description}</div>
                  {disease.category && (
                    <div className="text-xs text-purple-600 mt-1">{disease.category}</div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {isSearching && (
        <div className="px-8 py-4 bg-yellow-50 border-b border-yellow-200">
          <div className="flex items-center space-x-2">
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-500"></div>
            <span className="text-sm text-gray-600">Searching diseases...</span>
          </div>
        </div>
      )}

      {searchError && (
        <div className="px-8 py-4 bg-red-50 border-b border-red-200">
          <div className="text-sm text-red-600">{searchError}</div>
        </div>
      )}

      {/* Main Content Area */}
      <div className="flex flex-1 px-8 py-6 overflow-hidden space-x-6">
        {/* Left Sidebar Toggle Button - Fixed Position */}
        {!sidebarVisible && (
          <button
            onClick={() => setSidebarVisible(true)}
            className="fixed left-6 top-1/2 transform -translate-y-1/2 z-50 bg-blue-600 hover:bg-blue-700 text-white p-3 rounded-r-lg shadow-xl transition-all duration-300 flex items-center space-x-2"
            title="Show Disease Browser"
          >
            <span className="text-sm font-medium">Diseases</span>
            <ChevronRightIcon className="h-5 w-5" />
          </button>
        )}

        {/* Left Sidebar - Disease Analysis Controls */}
        {sidebarVisible && (
          <div className="w-80 bg-white rounded-xl shadow-lg border border-gray-200 flex flex-col relative">
            <button
              onClick={() => setSidebarVisible(false)}
              className="absolute right-3 top-3 z-10 bg-gray-100 hover:bg-gray-200 rounded-full p-1.5 transition-colors"
              title="Hide Disease Browser"
            >
              <EyeSlashIcon className="h-4 w-4 text-gray-500" />
            </button>
            
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-bold text-gray-900">Disease Categories</h2>
              </div>
            </div>
            
            <div className="flex-1 p-6 space-y-6 overflow-y-auto custom-scrollbar">
              {/* Disease Categories */}
              <div className="space-y-4">
                {categoriesLoading ? (
                  <div className="space-y-3">
                    {[...Array(6)].map((_, i) => (
                      <div key={i} className="animate-pulse">
                        <div className="h-12 bg-gray-200 rounded-xl"></div>
                      </div>
                    ))}
                  </div>
                ) : (
                  categories.map((category: any) => (
                    <button
                      key={category.id}
                      onClick={() => setSelectedCategory(category.id)}
                      className={`w-full p-4 text-left rounded-xl border-2 transition-all duration-200 ${
                        selectedCategory === category.id
                          ? 'border-blue-500 bg-blue-50 shadow-md'
                          : 'border-gray-200 bg-white hover:border-gray-300 hover:shadow-sm'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <h3 className="font-semibold text-gray-900">{category.name}</h3>
                        <span className={`px-3 py-1 text-xs font-medium rounded-full ${
                          selectedCategory === category.id
                            ? 'bg-blue-200 text-blue-800'
                            : 'bg-gray-100 text-gray-600'
                        }`}>
                          {category.disease_count} diseases
                        </span>
                      </div>
                    </button>
                  ))
                )}
              </div>

              {/* Priority Criteria */}
              {selectedCategory && (
                <div className="space-y-4">
                  <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">Priority Criteria</h3>
                  
                  <div className="space-y-4">
                    {/* Prevalence Weight */}
                    <div className="space-y-2">
                      <label className="block text-sm font-medium text-gray-700">
                        Prevalence Weight: {priorityRequest.prevalence_weight || 0.5}
                      </label>
                      <input
                        type="range"
                        min="0"
                        max="1"
                        step="0.1"
                        value={priorityRequest.prevalence_weight || 0.5}
                        onChange={(e) => setPriorityRequest({
                          ...priorityRequest,
                          prevalence_weight: parseFloat(e.target.value)
                        })}
                        className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                      />
                    </div>

                    {/* Severity Weight */}
                    <div className="space-y-2">
                      <label className="block text-sm font-medium text-gray-700">
                        Severity Weight: {priorityRequest.severity_weight || 0.3}
                      </label>
                      <input
                        type="range"
                        min="0"
                        max="1"
                        step="0.1"
                        value={priorityRequest.severity_weight || 0.3}
                        onChange={(e) => setPriorityRequest({
                          ...priorityRequest,
                          severity_weight: parseFloat(e.target.value)
                        })}
                        className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                      />
                    </div>

                    {/* Unmet Need Weight */}
                    <div className="space-y-2">
                      <label className="block text-sm font-medium text-gray-700">
                        Unmet Need Weight: {priorityRequest.unmet_need_weight || 0.2}
                      </label>
                      <input
                        type="range"
                        min="0"
                        max="1"
                        step="0.1"
                        value={priorityRequest.unmet_need_weight || 0.2}
                        onChange={(e) => setPriorityRequest({
                          ...priorityRequest,
                          unmet_need_weight: parseFloat(e.target.value)
                        })}
                        className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Prioritize Button */}
              {selectedCategory && (
                <div className="pt-4">
                  <button
                    onClick={handlePrioritizeClick}
                    disabled={prioritizeMutation.isPending}
                    className="w-full flex items-center justify-center px-6 py-4 bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 text-white font-medium rounded-xl transition-all duration-200 transform hover:scale-105 shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {prioritizeMutation.isPending ? (
                      <>
                        <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-current" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Prioritizing...
                      </>
                    ) : (
                      "Run Prioritization Analysis"
                    )}
                  </button>
                </div>
              )}

              {/* Database Stats */}
              <div className="bg-gradient-to-r from-gray-50 to-blue-50 rounded-xl p-4 border border-gray-200">
                <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-3">Database Stats</h3>
                {stats ? (
                  <div className="grid grid-cols-2 gap-4">
                    <div className="text-center">
                      <div className="text-2xl font-bold text-blue-600">{stats.total_diseases}</div>
                      <div className="text-xs text-gray-600">Total Diseases</div>
                    </div>
                    <div className="text-center">
                      <div className="text-2xl font-bold text-green-600">{stats.total_biomarkers}</div>
                      <div className="text-xs text-gray-600">Biomarkers</div>
                    </div>
                  </div>
                ) : (
                  <div className="animate-pulse">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="h-12 bg-gray-200 rounded"></div>
                      <div className="h-12 bg-gray-200 rounded"></div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Center Content Area */}
        <div className="flex-1 bg-white rounded-xl shadow-lg border border-gray-200 flex flex-col min-w-0 overflow-hidden">
          {analysisResults.length === 0 ? (
            <div className="flex items-center justify-center h-full p-8">
              <div className="text-center max-w-xl">
                <div className="text-6xl mb-6">🩺</div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">No Analysis Results</h3>
                <p className="text-gray-600 leading-relaxed">Select a disease category and run prioritization analysis to get started</p>
              </div>
            </div>
          ) : (
            <div className="flex-1 overflow-y-auto custom-scrollbar p-6">
              <div className="space-y-6">
                <div className="flex items-center justify-between">
                  <h2 className="text-2xl font-bold text-gray-900 flex items-center">
                    <span className="text-2xl mr-3">📊</span>
                    Disease Analysis Results
                  </h2>
                  <button className="flex items-center space-x-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors">
                    <ArrowDownTrayIcon className="h-4 w-4" />
                    <span className="text-sm font-medium">Export</span>
                  </button>
                </div>

                <div className="grid gap-4">
                  {analysisResults.map((result: any, index) => (
                    <div key={result.disease_id || index} className="bg-gradient-to-r from-white to-blue-50 rounded-xl shadow-lg border border-gray-200 p-6">
                      <div className="flex items-center justify-between mb-4">
                        <div>
                          <h3 className="text-lg font-bold text-gray-900">{result.disease_name}</h3>
                          <p className="text-sm text-gray-600">{result.description}</p>
                        </div>
                        <div className="text-center">
                          <div className="text-2xl font-bold text-blue-600">
                            {result.priority_score ? (result.priority_score * 100).toFixed(0) : 'N/A'}
                          </div>
                          <div className="text-xs text-gray-500">Priority Score</div>
                        </div>
                      </div>
                      
                      <div className="grid grid-cols-3 gap-4 mb-4">
                        <div className="text-center p-3 bg-white rounded-lg">
                          <div className="text-sm font-semibold text-green-700">
                            {result.prevalence ? (result.prevalence * 100).toFixed(1) : 'N/A'}%
                          </div>
                          <div className="text-xs text-gray-500">Prevalence</div>
                        </div>
                        <div className="text-center p-3 bg-white rounded-lg">
                          <div className="text-sm font-semibold text-orange-700">
                            {result.severity_score != null ? result.severity_score.toFixed(1) : 'N/A'}
                          </div>
                          <div className="text-xs text-gray-500">Severity</div>
                        </div>
                        <div className="text-center p-3 bg-white rounded-lg">
                          <div className="text-sm font-semibold text-red-700">
                            {result.unmet_need_score != null ? result.unmet_need_score.toFixed(1) : 'N/A'}
                          </div>
                          <div className="text-xs text-gray-500">Unmet Need</div>
                        </div>
                      </div>

                      <div className="mb-4">
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div 
                            className="bg-gradient-to-r from-blue-500 to-purple-600 h-2 rounded-full" 
                            style={{width: `${result.priority_score ? (result.priority_score * 100) : 0}%`}}
                          />
                        </div>
                      </div>

                      <button
                        onClick={() => {
                          setSelectedDisease(result.disease_id);
                          handleBiomarkerAnalysis();
                        }}
                        className="w-full px-4 py-2 bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white font-medium rounded-lg transition-all duration-200 transform hover:scale-105"
                      >
                        Analyze Biomarkers
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Right Panel Toggle Button */}
        {!resultsVisible && (
          <button
            onClick={() => setResultsVisible(true)}
            className="fixed right-6 top-1/2 transform -translate-y-1/2 z-50 bg-purple-600 hover:bg-purple-700 text-white p-3 rounded-l-lg shadow-xl transition-all duration-300 flex items-center space-x-2"
            title="Show Analysis Panel"
          >
            <span className="text-sm font-medium">Analysis</span>
            <EyeIcon className="h-5 w-5" />
          </button>
        )}

        {/* Right Analysis Panel */}
        {resultsVisible && (
          <div className="w-72 space-y-4 flex-shrink-0 relative">
            <button
              onClick={() => setResultsVisible(false)}
              className="absolute right-3 top-3 z-10 bg-gray-100 hover:bg-gray-200 rounded-full p-1.5 transition-colors"
              title="Hide Analysis Panel"
            >
              <EyeSlashIcon className="h-4 w-4 text-gray-500" />
            </button>
            
            {/* Biomarker Results */}
            <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-4">
              <h3 className="text-base font-bold text-gray-900 mb-4">Biomarker Analysis</h3>
              
              {biomarkerResults.length > 0 ? (
                <div className="space-y-3">
                  {biomarkerResults.slice(0, 3).map((biomarker: any, index) => (
                    <div key={index} className="p-3 bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg border border-green-200">
                      <div className="flex items-center justify-between mb-2">
                        <h4 className="font-semibold text-gray-900 text-sm">{biomarker.marker_name}</h4>
                        <span className="text-xs px-2 py-1 bg-green-100 text-green-800 rounded-full">
                          {biomarker.confidence != null ? biomarker.confidence.toFixed(1) : 'N/A'}%
                        </span>
                      </div>
                      <p className="text-xs text-gray-600">{biomarker.description}</p>
                      <div className="mt-2">
                        <div className="text-xs text-gray-500 mb-1">Association Strength</div>
                        <div className="w-full bg-gray-200 rounded-full h-1.5">
                          <div 
                            className="bg-green-500 h-1.5 rounded-full" 
                            style={{width: `${biomarker.association_strength * 100}%`}}
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <div className="text-3xl mb-3">🧪</div>
                  <p className="text-sm">Run biomarker analysis to view results</p>
                </div>
              )}
            </div>

            {/* Action Panel */}
            <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-4">
              <h3 className="text-base font-bold text-gray-900 mb-4">Actions</h3>
              
              {analysisResults.length > 0 ? (
                <div className="space-y-3">
                  <button className="w-full px-4 py-3 bg-gradient-to-r from-green-500 to-emerald-600 text-white font-medium text-sm rounded-lg hover:from-green-600 hover:to-emerald-700 transition-all duration-200 transform hover:scale-105 shadow-md">
                    Export Results
                  </button>
                  <button className="w-full px-4 py-3 bg-gradient-to-r from-blue-500 to-purple-600 text-white font-medium text-sm rounded-lg hover:from-blue-600 hover:to-purple-700 transition-all duration-200 transform hover:scale-105 shadow-md">
                    Save Analysis
                  </button>
                  <button className="w-full px-4 py-3 bg-gradient-to-r from-orange-500 to-red-600 text-white font-medium text-sm rounded-lg hover:from-orange-600 hover:to-red-700 transition-all duration-200 transform hover:scale-105 shadow-md">
                    Schedule Report
                  </button>
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <div className="text-3xl mb-3">⚙️</div>
                  <p className="text-sm">Run analysis to view actions</p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}